import Paper from '@mui/material/Paper';
import React, { ChangeEvent, useState } from 'react';
import { MHMTextField } from './MHM/MHMTextField';
import { ErrorText, MHMTypography, TitleText } from './MHM/MHMTypography';
import { useTranslation } from '../../contexts/TranslationContext';
import { MHMSelect } from './MHM/MHMSelect';
import { MenuItem, useTheme } from '@mui/material';
import { device_controller } from '../../controllers/device_controller';
import { useForm, useStore } from '@tanstack/react-form';
import { custom, literal, number, object, pipe, union } from 'valibot';

const READING_INTERVAL_SECONDS_MIN = 60;
const READING_INTERVAL_SECONDS_MAX = 720 * 60;
const TRANSMISSION_INTERVAL_SECONDS_MIN = 720;
const TRANSMISSION_INTERVAL_SECONDS_MAX = 72 * 3600;

function calculateIntervals(
  readingPeriod: number,
  readingPeriodUnit: number,
  transmissionPeriod: number,
  transmissionPeriodUnit: number,
) {
  const readingIntervalSeconds = readingPeriod * readingPeriodUnit;
  const transmissionIntervalSeconds =
    transmissionPeriod * transmissionPeriodUnit;
  const maxReadingFrequency = Math.min(
    transmissionIntervalSeconds,
    READING_INTERVAL_SECONDS_MAX,
  );

  return {
    readingIntervalSeconds,
    transmissionIntervalSeconds,
    maxReadingFrequency,
  };
}

export const BatteryLife: React.FC = () => {
  const { t } = useTranslation();
  const {
    assets: { logoURL },
  } = useTheme();

  const [expectedBatteryLife, setExpectedBatteryLife] = useState<string | null>(
    null,
  );
  const [submitCount, setSubmitCount] = useState(0);

  const form = useForm({
    defaultValues: {
      readingPeriodUnit: 60,
      readingPeriod: 15,
      transmissionPeriodUnit: 3600,
      transmissionPeriod: 24,
      sensorType: 'radar' as 'radar' | 'ultrasound',
    },
    validators: {
      onChange: ({ value, formApi }) => {
        const {
          readingIntervalSeconds,
          transmissionIntervalSeconds,
          maxReadingFrequency,
        } = calculateIntervals(
          value.readingPeriod,
          value.readingPeriodUnit,
          value.transmissionPeriod,
          value.transmissionPeriodUnit,
        );

        const schema = object({
          readingPeriodUnit: union([
            literal(86400),
            literal(3600),
            literal(60),
          ]),
          readingPeriod: pipe(
            number('Please provide a valid number!'),
            custom(
              () => readingIntervalSeconds >= READING_INTERVAL_SECONDS_MIN,
              'Not in the allowable range minutes',
            ),
            custom(
              () => readingIntervalSeconds <= maxReadingFrequency,
              'Not in the allowable range minutes > transmission',
            ),
          ),
          transmissionPeriodUnit: union([
            literal(86400),
            literal(3600),
            literal(60),
          ]),
          transmissionPeriod: pipe(
            number('Please provide a valid number!'),
            custom(
              () =>
                transmissionIntervalSeconds >=
                  TRANSMISSION_INTERVAL_SECONDS_MIN &&
                transmissionIntervalSeconds <=
                  TRANSMISSION_INTERVAL_SECONDS_MAX,
              'Not in the allowable range hours',
            ),
          ),
          sensorType: union([literal('radar'), literal('ultrasound')]),
        });

        return formApi.parseValuesWithSchema(schema);
      },
    },
    listeners: {
      onChange: ({ formApi }) => {
        if (!formApi.state.isValid) {
          return;
        }

        formApi.handleSubmit();
      },
      onChangeDebounceMs: 300,
      onMount: ({ formApi }) => {
        device_controller
          .get_battery_life(formApi.state.values)
          .then(v => setExpectedBatteryLife(v));
      },
    },
    onSubmit: ({ value }) => {
      setSubmitCount(prev => prev + 1);
      device_controller.get_battery_life(value).then(v => {
        setExpectedBatteryLife(v);
        setSubmitCount(prev => prev - 1);
      });
    },
  });

  const errorInterpolationValues = useStore(form.store, state => {
    const { maxReadingFrequency } = calculateIntervals(
      state.values.readingPeriod,
      state.values.readingPeriodUnit,
      state.values.transmissionPeriod,
      state.values.transmissionPeriodUnit,
    );

    return {
      minHours: '0.2',
      maxHours: '72.0',
      minMinutes: '1.0',
      maxMinutes: '720.0',
      minReadingFrequency: '1.0',
      maxReadingFrequency: (maxReadingFrequency / 60).toFixed(1),
    };
  });

  const isFormValid = useStore(
    form.store,
    state => state.isPristine || (state.isValid && state.isSubmitSuccessful),
  );

  return (
    <Paper
      sx={{
        maxWidth: 800,
        margin: '1rem',
        padding: '2rem',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img style={{ height: 100 }} src={logoURL} alt="MHM Logo" />
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          paddingBottom: '1rem',
        }}
      >
        <TitleText
          variant="h5"
          sx={{ color: '#000099', paddingBottom: '1rem' }}
        >
          {t('Battery Life Calculator')}
        </TitleText>
        <MHMTypography variant="body1">
          {t(
            'Input desired normal operating frequencies for the device to calculate its expected battery life',
          )}
        </MHMTypography>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          position: 'relative',
          paddingBottom: '2rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            position: 'relative',
            paddingBottom: '2rem',
          }}
        >
          <MHMTypography variant="body1" style={{ fontWeight: 600 }}>
            {t('Sensor type')}
          </MHMTypography>
          <form.Field name="sensorType">
            {field => (
              <MHMSelect
                id="sensor-type-select"
                style={{ minWidth: '7rem' }}
                value={field.state.value}
                onChange={e =>
                  field.handleChange(e.target.value as 'radar' | 'ultrasound')
                }
                size="small"
              >
                <MenuItem value={'radar'}>{t('Radar')}</MenuItem>
                <MenuItem value={'ultrasound'}>{t('Ultrasound')}</MenuItem>
              </MHMSelect>
            )}
          </form.Field>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            position: 'relative',
            paddingBottom: '2rem',
          }}
        >
          <MHMTypography variant="body1" style={{ fontWeight: 600 }}>
            {t('Take a measurement every')}
          </MHMTypography>
          <div style={{ flex: 1, flexDirection: 'row' }}>
            <form.Field name="readingPeriod">
              {field => (
                <MHMTextField
                  id="readingPeriod"
                  style={{ marginRight: '1rem' }}
                  value={isNaN(field.state.value) ? '' : field.state.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    field.handleChange(Number(e.target.value));
                  }}
                />
              )}
            </form.Field>
            <form.Field name="readingPeriodUnit">
              {field => (
                <MHMSelect
                  style={{ minWidth: '7rem' }}
                  value={String(field.state.value)}
                  onChange={e => field.handleChange(Number(e.target.value))}
                  size="small"
                  id="readingPeriodUnit"
                >
                  <MenuItem value={86400}>{t('Days')}</MenuItem>
                  <MenuItem value={3600}>{t('Hours')}</MenuItem>
                  <MenuItem value={60}>{t('Minutes')}</MenuItem>
                </MHMSelect>
              )}
            </form.Field>
          </div>
          <form.Subscribe selector={state => state.errorMap.onChange}>
            {errors => (
              <ErrorText
                variant="body2"
                style={{
                  position: 'absolute',
                  bottom: 5,
                  width: 'max-content',
                  maxWidth: 800,
                }}
              >
                {t(
                  errors?.readingPeriod?.[0].message ?? '',
                  errorInterpolationValues,
                )}
              </ErrorText>
            )}
          </form.Subscribe>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            position: 'relative',
            paddingBottom: '1rem',
          }}
        >
          <MHMTypography variant="body1" style={{ fontWeight: 600 }}>
            {t('Transmit data every')}
          </MHMTypography>
          <div style={{ flex: 1, flexDirection: 'row' }}>
            <form.Field name="transmissionPeriod">
              {field => (
                <MHMTextField
                  id="transmissionPeriod"
                  style={{ marginRight: '1rem' }}
                  value={isNaN(field.state.value) ? '' : field.state.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    field.handleChange(Number(e.target.value))
                  }
                />
              )}
            </form.Field>
            <form.Field name="transmissionPeriodUnit">
              {field => (
                <MHMSelect
                  style={{ minWidth: '7rem' }}
                  value={String(field.state.value)}
                  onChange={e => field.handleChange(Number(e.target.value))}
                  size="small"
                  id="transmissionPeriodUnit"
                >
                  <MenuItem value={86400}>{t('Days')}</MenuItem>
                  <MenuItem value={3600}>{t('Hours')}</MenuItem>
                  <MenuItem value={60}>{t('Minutes')}</MenuItem>
                </MHMSelect>
              )}
            </form.Field>
          </div>
        </div>
        <div
          style={{
            position: 'absolute',
            width: 'max-content',
            maxWidth: 800,
            top: '82.5%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <form.Subscribe selector={state => state.errorMap.onChange}>
            {errors => (
              <ErrorText variant="body2" style={{ paddingTop: '0.75rem' }}>
                {t(
                  errors?.transmissionPeriod?.[0].message ?? '',
                  errorInterpolationValues,
                )}
              </ErrorText>
            )}
          </form.Subscribe>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          paddingBottom: '1rem',
        }}
      >
        <MHMTypography variant="body1">
          {t(
            'At this rate of measurements and transmissions, the device battery will last',
          )}
        </MHMTypography>
        <TitleText
          variant="h4"
          style={{ paddingTop: '1rem' }}
          data-testid="battery-life-result"
        >
          {isFormValid && expectedBatteryLife !== '-1' && submitCount === 0
            ? expectedBatteryLife
            : '--'}{' '}
          {t('years*')}
        </TitleText>
      </div>
      <MHMTypography variant="body2">
        {t(
          'Disclaimer: this calculator is an estimate only and assumes the device begins with 100% battery, maintains these frequencies its entire lifetime, and operates within the permitted temperature range: -40 to +60°C / -40 to +140°F. Due to the lifetime of other components, the device’s maximum operational life is 10 years.',
        )}
      </MHMTypography>
    </Paper>
  );
};
